import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * The current user's role on the open project (owner | editor | commenter |
 * viewer), provided by ProjectView from the server's `myRole` annotation.
 *
 * Components anywhere under the project tree read it via useProjectAccess()
 * instead of prop drilling. Outside a provider (tests, legacy paths) it
 * defaults to full owner access, matching pre-sharing behavior. The server
 * enforces every permission regardless — these flags only shape the UI.
 */

const OWNER_ACCESS = Object.freeze({
  role: 'owner',
  isOwner: true,
  canEdit: true,
  canComment: true,
});

const ProjectAccessContext = createContext(OWNER_ACCESS);

export const useProjectAccess = () => useContext(ProjectAccessContext);

export const ProjectAccessProvider = ({ role, children }) => {
  const value = useMemo(() => {
    const resolved = role || 'owner';
    return {
      role: resolved,
      isOwner: resolved === 'owner',
      canEdit: resolved === 'owner' || resolved === 'editor',
      canComment: resolved !== 'viewer',
    };
  }, [role]);

  return <ProjectAccessContext.Provider value={value}>{children}</ProjectAccessContext.Provider>;
};

ProjectAccessProvider.propTypes = {
  role: PropTypes.oneOf(['owner', 'editor', 'commenter', 'viewer']),
  children: PropTypes.node.isRequired,
};

ProjectAccessProvider.defaultProps = {
  role: 'owner',
};

export default ProjectAccessContext;
